import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Box, TextField, Button as MuiButton } from '@mui/material';
import { Card, Row, Col, Collapse, Spinner, Button } from 'react-bootstrap';
import useAxios from '../../services/AxiosConfig';
import { useSelector, useDispatch } from 'react-redux';
import { formatDateString } from '../../utils/dateUtils';
import { fetchDeviceTrackingData } from '../../utils/deviceTrackingUtils';
import { showAlert } from '../../components/PerinHealthAlertSlice';
import { getBatteryColor, getBatteryStatusText } from '../../utils/batteryUtils';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const DeviceInformationContent = ({ deviceData, onUpdate }) => {
    const dispatch = useDispatch();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);
    const userDateFormat = useSelector((state) => state.userInfo.dateFormat);
    const userId = useSelector((state) => state.userInfo.id);
    const [showClinicalModal, setShowClinicalModal] = useState(false);
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [showCheckoutModal, setShowCheckoutModal] = useState(false);
    const [deviceNotes, setDeviceNotes] = useState([]);
    const [newNote, setNewNote] = useState('');
    const [checkoutLogData, setCheckoutLogData] = useState([]);
    const [checkoutForm, setCheckoutForm] = useState({
        name: '',
        reason: '',
        system: ''
    });
    const [clinicalForm, setClinicalForm] = useState({
        studyId: '',
        studySite: '',
        collectionInfo: ''
    });
    const [clinicalInfo, setClinicalInfo] = useState(null);
    
    // Device Monitoring Tab States
    const [activeTab, setActiveTab] = useState('monitoring');
    const [batteryData, setBatteryData] = useState(null);
    const [rawBatteryData, setRawBatteryData] = useState(null);
    const [signalQualityData, setSignalQualityData] = useState(null);
    const [rawSignalQualityData, setRawSignalQualityData] = useState(null);
    const [alertsData, setAlertsData] = useState(null);
    const [batteryChartView, setBatteryChartView] = useState('hourly');
    const [signalQualityChartView, setSignalQualityChartView] = useState('hourly');
    const [patchMetadata, setPatchMetadata] = useState(null);
    const [latestRecordingTimestamp, setLatestRecordingTimestamp] = useState(null);
    const [isDeviceOnTrack, setIsDeviceOnTrack] = useState(null);
    const [expandedAlerts, setExpandedAlerts] = useState({});
    const [selfTestResults, setSelfTestResults] = useState(null);
    const [loadingSelfTest, setLoadingSelfTest] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [actionType, setActionType] = useState(null);
    const [processingAction, setProcessingAction] = useState(null);
    const [actionLogs, setActionLogs] = useState([]);
    const [loadingActions, setLoadingActions] = useState(false);
    const [modalityTrackingStatus, setModalityTrackingStatus] = useState({});
    const [rebootCount, setRebootCount] = useState(null);
    const [lastRebootTimestamp, setLastRebootTimestamp] = useState(null);
    const [deviceReadings, setDeviceReadings] = useState([]);
    const [modalityLastTransmission, setModalityLastTransmission] = useState({});

    const fetchClinicalInfo = useCallback(async () => {
        try {
            const response = await httpService.get(`/device/clinical-info/${deviceData.id}`);
            if (response.data?.data) {
                setClinicalInfo(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching clinical information:', error);
        }
    }, [httpService, deviceData?.id]);

    const fetchDeviceNotes = useCallback(async () => {
        try {
            const response = await httpService.get(`/device/notes/${deviceData.id}`);
            if (Array.isArray(response.data.data)) {
                const formattedNotes = response.data.data.map(noteObj => ({
                    note: noteObj.notes,
                    timestamp: noteObj.createdAt
                }));
                setDeviceNotes(formattedNotes);
            } else {
                setDeviceNotes([]);
            }
        } catch (error) {
            console.error('Error fetching device notes:', error);
            setDeviceNotes([]);
        }
    }, [httpService, deviceData?.id]);

    const fetchCheckoutLog = useCallback(async () => {
        try {
            const response = await httpService.get(`/device/checkout-log/${deviceData.id}`);
            if (Array.isArray(response.data.data)) {
                const formattedData = response.data.data.map(item => ({
                    ...item,
                    checkoutDate: item.checkoutDate ? formatDateString(item.checkoutDate, userDateFormat) : "-",
                    checkinDate: item.checkinDate ? formatDateString(item.checkinDate, userDateFormat) : "-"
                }));
                setCheckoutLogData(formattedData);
            }
        } catch (err) {
            console.error('Error fetching checkout log:', err);
            setCheckoutLogData([]);
        }
    }, [httpService, deviceData?.id, userDateFormat]);

    // Fetch data when component mounts or device data changes
    useEffect(() => {
        if (deviceData?.id) {
            fetchClinicalInfo();
            fetchDeviceNotes();
            fetchCheckoutLog();
        }
    }, [deviceData?.id, fetchClinicalInfo, fetchDeviceNotes, fetchCheckoutLog]);


    // Fetch signal quality data for monitoring tab
    useEffect(() => {
        const fetchSignalQualityData = async () => {
            if (!deviceData?.patientId) return;

            try {
                const response = await httpService.get(`/device/sqi-readings/${deviceData.patientId}`);
                
                if ((response.data?.success || response.data?.code === 200) && response.data?.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
                    setRawSignalQualityData(response.data.data);
                } else {
                    setRawSignalQualityData([]);
                }
            } catch (err) {
                console.error('Error fetching signal quality data:', err);
                setRawSignalQualityData([]);
            }
        };

        if (activeTab === 'monitoring') {
            fetchSignalQualityData();
        }
    }, [deviceData?.patientId, httpService, activeTab]);

    // Fetch alerts data for monitoring tab
    useEffect(() => {
        const fetchAlertsData = async () => {
            if (!deviceData?.patientId) return;

            try {
                const response = await httpService.get(`/alerts/${deviceData.patientId}?deviceStatus=true`);
                
                if ((response.data?.success || response.data?.code === 200) && response.data?.data && Array.isArray(response.data.data)) {
                    setAlertsData(response.data.data);
                } else {
                    setAlertsData([]);
                }
            } catch (err) {
                console.error('Error fetching alerts data:', err);
                setAlertsData([]);
            }
        };

        if (activeTab === 'monitoring') {
            fetchAlertsData();
        }
    }, [deviceData?.patientId, httpService, activeTab]);

    // Fetch device readings for last transmission times
    useEffect(() => {
        const fetchDeviceReadings = async () => {
            if (!deviceData?.patientId) return;

            try {
                const response = await httpService.get(`device/readings/latest/${deviceData.patientId}`);
                
                if ((response.data?.success || response.data?.code === 200) && 
                    response.data?.data && 
                    Array.isArray(response.data.data) && 
                    response.data.data.length > 0) {
                    
                    const readings = response.data.data;
                    setDeviceReadings(readings);
                    
                    // Calculate last transmission time for each modality using modalityType
                    const modalityMap = {
                        'ecg': 'ecg',
                        'ppg': 'ppg',
                        'ausc': 'ausc',
                        'Temperature': 'temp',
                        'bioz': 'bioz'
                    };
                    
                    const lastTransmissions = {};
                    Object.entries(modalityMap).forEach(([modalityType, scheduleKey]) => {
                        const modalityReadings = readings.filter(r => r.modalityType === modalityType);
                        if (modalityReadings.length > 0) {
                            // Get the most recent reading for this modality
                            const sortedReadings = [...modalityReadings].sort((a, b) => 
                                new Date(b.completedDate) - new Date(a.completedDate)
                            );
                            lastTransmissions[scheduleKey] = sortedReadings[0].completedDate;
                        }
                    });
                    
                    setModalityLastTransmission(lastTransmissions);
                } else {
                    setDeviceReadings([]);
                    setModalityLastTransmission({});
                }
            } catch (err) {
                console.error('Error fetching device readings:', err);
                setDeviceReadings([]);
                setModalityLastTransmission({});
            }
        };

        fetchDeviceReadings();
    }, [deviceData?.patientId, httpService]);

    // Fetch health patch metadata (consolidated: battery, reboot count, device schedule)
    useEffect(() => {
        const fetchPatchMetadata = async () => {
            if (!deviceData?.patientId) return;

            try {
                // Fetch full metadata array to analyze reboot history and get battery data
                const metadataArrayResponse = await httpService.get(`/measurements/patch/metadata/${deviceData.patientId}`);
                
                if ((metadataArrayResponse.data?.success || metadataArrayResponse.data?.code === 200) && 
                    metadataArrayResponse.data?.data && 
                    Array.isArray(metadataArrayResponse.data.data) && 
                    metadataArrayResponse.data.data.length > 0) {
                    
                    const metadataArray = metadataArrayResponse.data.data;
                    
                    // Sort by createdDate descending to get most recent first
                    const sortedMetadata = [...metadataArray].sort((a, b) => 
                        new Date(b.createdDate) - new Date(a.createdDate)
                    );
                    
                    // Use the latest metadata for device schedule and display
                    const latestMetadata = sortedMetadata[0];
                    setPatchMetadata(latestMetadata);
                    
                    // Process battery data for monitoring tab
                    const batteryLevelData = metadataArray.map(item => ({
                        batteryLevel: Math.round((item.batteryLevel || 0) * 100), // Convert decimal to percentage (0.87 -> 87)
                        createdDate: item.createdDate || new Date().toISOString()
                    }));
                    setRawBatteryData(batteryLevelData);
                    
                    // Process reboot count
                    const currentRebootCount = latestMetadata.rebootCount || null;
                    setRebootCount(currentRebootCount);
                    
                    // Find when the reboot count last increased
                    let rebootTimestamp = null;
                    if (sortedMetadata.length > 1 && currentRebootCount !== null) {
                        // Look through sorted metadata to find where reboot count changed
                        for (let i = 0; i < sortedMetadata.length - 1; i++) {
                            const currentCount = sortedMetadata[i].rebootCount || 0;
                            const previousCount = sortedMetadata[i + 1].rebootCount || 0;
                            
                            if (currentCount > previousCount) {
                                // Found where reboot count increased
                                rebootTimestamp = sortedMetadata[i].createdDate;
                                break;
                            }
                        }
                        
                        // If no increase found, use the latest metadata timestamp
                        if (!rebootTimestamp) {
                            rebootTimestamp = latestMetadata.createdDate;
                        }
                    } else {
                        // Single metadata entry or no reboot count, use latest timestamp
                        rebootTimestamp = latestMetadata.createdDate || null;
                    }
                    
                    setLastRebootTimestamp(rebootTimestamp);
                } else {
                    // If array is empty, try fetching latest metadata as fallback
                    const latestMetadataResponse = await httpService.get(`/measurements/patch/metadata/latest/${deviceData.patientId}`);
                    if ((latestMetadataResponse.data?.success || latestMetadataResponse.data?.code === 200) && latestMetadataResponse.data?.data) {
                        const latestData = latestMetadataResponse.data.data;
                        setPatchMetadata(latestData);
                        setRebootCount(latestData.rebootCount || null);
                        setLastRebootTimestamp(latestData.createdDate || null);
                        
                        // Set battery data from single latest entry
                        const batteryLevelData = [{
                            batteryLevel: Math.round((latestData.batteryLevel || 0) * 100),
                            createdDate: latestData.createdDate || new Date().toISOString()
                        }];
                        setRawBatteryData(batteryLevelData);
                } else {
                    setPatchMetadata(null);
                        setRebootCount(null);
                        setLastRebootTimestamp(null);
                        setRawBatteryData([]);
                    }
                }
            } catch (err) {
                console.error('Error fetching patch metadata:', err);
                setPatchMetadata(null);
                setRebootCount(null);
                setLastRebootTimestamp(null);
                setRawBatteryData([]);
            }
        };

        // Always fetch metadata
            fetchPatchMetadata();
    }, [deviceData?.patientId, httpService]);

    // Fetch latest recording timestamp from health evaluation data
    useEffect(() => {
        const fetchLatestRecordingTimestamp = async () => {
            if (!deviceData?.patientId) {
                setIsDeviceOnTrack(null);
                return;
            }

            const { latestTimestamp, isOnTrack, hasMetadata, modalityStatus } = await fetchDeviceTrackingData(
                deviceData.patientId,
                httpService,
                patchMetadata
            );

            if (latestTimestamp) {
                setLatestRecordingTimestamp(latestTimestamp);
            }

            // If no metadata is available, we can't determine if device is on track
            if (!hasMetadata) {
                setIsDeviceOnTrack(null);
                return;
            }

            // If we have metadata, use the calculated on-track status
            setIsDeviceOnTrack(isOnTrack);
            setModalityTrackingStatus(modalityStatus || {});
        };

        fetchLatestRecordingTimestamp();
    }, [deviceData?.patientId, httpService, patchMetadata]);

    // Recalculate on-track status when patchMetadata changes
    useEffect(() => {
        const recalculateOnTrackStatus = async () => {
            if (!deviceData?.patientId) {
                setIsDeviceOnTrack(null);
                return;
            }

            if (!patchMetadata?.deviceSchedule) {
                setIsDeviceOnTrack(null);
                return;
            }

            const { isOnTrack, hasMetadata, modalityStatus } = await fetchDeviceTrackingData(
                deviceData.patientId,
                httpService,
                patchMetadata
            );
            
            // Only set the status if we have valid metadata
            if (hasMetadata) {
                setIsDeviceOnTrack(isOnTrack);
                setModalityTrackingStatus(modalityStatus || {});
            } else {
                setIsDeviceOnTrack(null);
            }
        };

        recalculateOnTrackStatus();
    }, [patchMetadata, deviceData?.patientId, httpService]);

    // Process battery data when raw data or view changes
    useEffect(() => {
        if (rawBatteryData !== null) {
            const processedData = processBatteryData(rawBatteryData, batteryChartView);
            setBatteryData(processedData);
        }
    }, [rawBatteryData, batteryChartView]);

    // Process signal quality data when raw data or view changes
    useEffect(() => {
        if (rawSignalQualityData !== null) {
            const processedData = processSignalQualityData(rawSignalQualityData, signalQualityChartView);
            setSignalQualityData(processedData);
        }
    }, [rawSignalQualityData, signalQualityChartView]);

    // Fetch self-test results for monitoring tab
    useEffect(() => {
        const fetchSelfTestResults = async () => {
            if (!deviceData?.patientId) return;

            setLoadingSelfTest(true);
            try {
                const response = await httpService.get(`/notifications/selfTestResults/${deviceData.patientId}`);
                if ((response.data?.code === 200 || response.data?.success) && Array.isArray(response.data?.data) && response.data.data.length > 0) {
                    setSelfTestResults(response.data.data[0]);
                } else {
                    setSelfTestResults(null);
                }
            } catch (err) {
                console.error('Error fetching self-test results:', err);
                setSelfTestResults(null);
            } finally {
                setLoadingSelfTest(false);
            }
        };

        if (activeTab === 'monitoring') {
            fetchSelfTestResults();
        }
    }, [deviceData?.patientId, httpService, activeTab]);

    // Fetch action history
    const fetchActionHistory = useCallback(async () => {
        if (!deviceData?.patientId) return;

        setLoadingActions(true);
        try {
            const response = await httpService.get(`/device/health-patch-actions/${deviceData.patientId}`);
            if ((response.data?.success || response.data?.code === 200) && Array.isArray(response.data?.data)) {
                setActionLogs(response.data.data);
            } else {
                setActionLogs([]);
            }
        } catch (err) {
            console.error('Error fetching action history:', err);
            setActionLogs([]);
        } finally {
            setLoadingActions(false);
        }
    }, [deviceData?.patientId, httpService]);

    // Fetch action history when device data changes
    useEffect(() => {
        if (deviceData?.patientId) {
            fetchActionHistory();
        }
    }, [deviceData?.patientId, fetchActionHistory]);

    const handleFormChange = (field) => (event) => {
        setClinicalForm({
            ...clinicalForm,
            [field]: event.target.value
        });
    };

    const handleCheckoutFormChange = (field) => (event) => {
        setCheckoutForm({
            ...checkoutForm,
            [field]: event.target.value
        });
    };

    const handleSubmitClinical = async () => {
        try {
            // Validate required fields with null checks
            if (!clinicalForm?.studyId || !clinicalForm.studyId.toString().trim()) {
                alert('Please enter a Study ID');
                return;
            }
            if (!clinicalForm?.studySite || !clinicalForm.studySite.toString().trim()) {
                alert('Please enter a Study Site');
                return;
            }

            // Check if we have a valid device ID
            if (!deviceData?.id) {
                console.error('Missing device ID:', deviceData);
                alert('Device ID not found. Please try again.');
                return;
            }

            // Ensure the form data is properly formatted
            const formattedForm = {
                studyId: clinicalForm.studyId.toString().trim(),
                studySite: clinicalForm.studySite.toString().trim(),
                collectionInfo: clinicalForm.collectionInfo ? clinicalForm.collectionInfo.toString().trim() : ''
            };

            // Make API call to update clinical information using id
            const response = await httpService.put(`/device/clinical-info/${deviceData.id}`, formattedForm);
            
            // Add response logging
            console.log('Clinical info update response:', response);

            // Fetch updated clinical information
            await fetchClinicalInfo();
            
            // Close modal and reset form on success
            setShowClinicalModal(false);
            setClinicalForm({ studyId: '', studySite: '', collectionInfo: '' });
            
            // Show success message
            alert('Clinical information updated successfully');
        } catch (error) {
            console.error('Error updating clinical information:', error);
            console.error('Error response:', error.response?.data);
            alert('Failed to update clinical information. Please try again.');
        }
    };

    const handleAddNote = async () => {
        try {
            if (!newNote.trim()) {
                alert('Please enter a note');
                return;
            }

            // Send the note in a proper JSON format
            await httpService.put(`/device/notes/${deviceData.id}`, {
                notes: newNote.trim()
            });

            // Refresh notes after adding
            await fetchDeviceNotes();
            setShowNotesModal(false);
            setNewNote('');
            alert('Note added successfully');
        } catch (error) {
            console.error('Error adding note:', error);
            alert('Failed to add note. Please try again.');
        }
    };

    const handleCheckout = async () => {
        try {
            // Validate form data
            if (!checkoutForm.name.trim()) {
                alert('Please enter a name');
                return;
            }
            if (!checkoutForm.reason.trim()) {
                alert('Please enter a reason');
                return;
            }
            if (!checkoutForm.system.trim()) {
                alert('Please enter a system');
                return;
            }

            await httpService.post(`/device/checkout/${deviceData.id}`, {
                userName: checkoutForm.name,
                reason: checkoutForm.reason,
                system: checkoutForm.system
            });

            // Update local device data with checkout information
            const updatedDeviceData = {
                ...deviceData,
                checkInOutStatus: 'Checked Out',
                teamMemberAssigned: checkoutForm.name,
                checkOutReason: checkoutForm.reason,
                checkOutSoftware: checkoutForm.system,
                checkOutDateTime: new Date().toISOString()
            };

            // Update the page
            if (onUpdate) onUpdate(updatedDeviceData);
            
            // Reset form and close modal
            setShowCheckoutModal(false);
            setCheckoutForm({ name: '', reason: '', system: '' });
            
            // Refresh the checkout log
            await fetchCheckoutLog();
            
            alert('Device checked out successfully');
        } catch (error) {
            console.error('Error during checkout:', error);
            alert('Failed to check out device. Please try again.');
        }
    };

    const handleCheckin = async () => {
        try {
            await httpService.post(`/device/checkin/${deviceData.id}`);
            
            // Update local device data
            const updatedDeviceData = {
                ...deviceData,
                checkInOutStatus: 'Checked In',
                teamMemberAssigned: null,
                checkOutReason: null,
                checkOutSoftware: null,
                checkOutDateTime: null
            };
            
            // Update the page
            if (onUpdate) onUpdate(updatedDeviceData);
            
            // Refresh the checkout log
            await fetchCheckoutLog();
            
            alert('Device checked in successfully');
        } catch (error) {
            console.error('Error during check-in:', error);
            alert('Failed to check in device. Please try again.');
        }
    };

    const handleOpenModal = () => {
        // If clinical info exists, prepopulate the form
        if (clinicalInfo && (clinicalInfo.studyId || clinicalInfo.studySite || clinicalInfo.collectionInfo)) {
            setClinicalForm({
                studyId: clinicalInfo.studyId || '',
                studySite: clinicalInfo.studySite || '',
                collectionInfo: clinicalInfo.collectionInfo || ''
            });
        } else {
            // Reset form if no clinical info exists
            setClinicalForm({
                studyId: '',
                studySite: '',
                collectionInfo: ''
            });
        }
        setShowClinicalModal(true);
    };

    const handleCloseModal = () => {
        setShowClinicalModal(false);
        // Reset form to empty state when closing
        setClinicalForm({
            studyId: '',
            studySite: '',
            collectionInfo: ''
        });
    };

    // Action handling functions
    const getActionDescription = (type) => {
        switch (type) {
            case 'self-test':
                return 'Initiate a remote self-test on this device?';
            case 'battery':
                return 'Request battery delivery for this device?';
            case 'reset':
                return 'Reset this device? This will clear all temporary data.';
            case 'firmware':
                return 'Push firmware update to this device?';
            default:
                return 'Proceed with this action?';
        }
    };

    const handleAction = (type) => {
        setActionType(type);
        setShowConfirmModal(true);
    };

    const confirmAction = async () => {
        setShowConfirmModal(false);
        setProcessingAction(actionType);

        const accountId = deviceData?.patientId;
        if (!accountId) {
            setProcessingAction(null);
            return;
        }

        try {
            // Handle self-test separately with the new endpoint
            if (actionType === 'self-test') {
                const response = await httpService.post(`/notifications/selfTest/${accountId}`);
                if ((response.data?.success || response.data?.code === 200) && response.status >= 200 && response.status < 300) {
                    // Also save the action to HealthPatchActions table
                    await httpService.post(`/device/health-patch-actions/${accountId}`, { 
                        deviceControl: 'Initiate Self-Test',
                        issuerId: userId
                    });

                    dispatch(showAlert({
                        header: 'Self-Test Initiated',
                        message: response.data?.message || 'Self-test has been successfully triggered for this device.',
                        type: 'success',
                        dismissable: false
                    }));

                    // Refresh action history and self-test results
                    await fetchActionHistory();
                    setTimeout(() => {
                        // Refresh self-test results if we're on monitoring tab
                        if (activeTab === 'monitoring') {
                            const fetchSelfTestResults = async () => {
                                try {
                                    const response = await httpService.get(`/notifications/selfTestResults/${accountId}`);
                                    if ((response.data?.code === 200 || response.data?.success) && Array.isArray(response.data?.data) && response.data.data.length > 0) {
                                        setSelfTestResults(response.data.data[0]);
                                    } else {
                                        setSelfTestResults(null);
                                    }
                                } catch (err) {
                                    console.error('Error fetching self-test results:', err);
                                    setSelfTestResults(null);
                                }
                            };
                            fetchSelfTestResults();
                        }
                    }, 30000); // Wait a bit for the test to complete
                } else {
                    dispatch(showAlert({
                        header: 'Self-Test Failed',
                        message: response.data?.message || 'Failed to trigger self-test. Please try again.',
                        type: 'danger',
                        dismissable: false
                    }));
                }
            } else if (actionType === 'firmware') {
                // Handle firmware update with the new endpoint
                const response = await httpService.post(`/notifications/softwareUpdate/${accountId}`);
                if ((response.data?.success || response.data?.code === 200) && response.status >= 200 && response.status < 300) {
                    // Also save the action to HealthPatchActions table
                    await httpService.post(`/device/health-patch-actions/${accountId}`, { 
                        deviceControl: 'Push Firmware Update',
                        issuerId: userId
                    });

                    dispatch(showAlert({
                        header: 'Software Update Initiated',
                        message: response.data?.message || 'Software update has been successfully triggered for this device.',
                        type: 'success',
                        dismissable: false
                    }));

                    // Refresh action history
                    await fetchActionHistory();
                } else {
                    dispatch(showAlert({
                        header: 'Software Update Failed',
                        message: response.data?.message || 'Failed to trigger software update. Please try again.',
                        type: 'danger',
                        dismissable: false
                    }));
                }
            } else {
                // Handle other actions (battery, reset) with the existing endpoint
                let deviceControl = '';

                switch (actionType) {
                    case 'battery':
                        deviceControl = 'Request Batteries';
                        break;
                    case 'reset':
                        deviceControl = 'Reset Device';
                        break;
                    default:
                        break;
                }

                await httpService.post(`/device/health-patch-actions/${accountId}`, { 
                    deviceControl,
                    issuerId: userId
                });
                
                dispatch(showAlert({
                    header: 'Action Completed',
                    message: `${deviceControl} has been successfully initiated.`,
                    type: 'success',
                    dismissable: false
                }));
                
                // Refresh action history to get the latest data from the server
                await fetchActionHistory();
            }
        } catch (err) {
            console.error('Error creating action:', err);
            
            dispatch(showAlert({
                header: 'Action Failed',
                message: 'An error occurred while processing your request. Please try again.',
                type: 'danger',
                dismissable: false
            }));
        } finally {
            setProcessingAction(null);
        }
    };


    // Tab styling function
    const tabStyle = (isActive) => ({
        paddingLeft: 20,
        paddingRight: 20,
        paddingTop: 4,
        paddingBottom: 4,
        background: isActive ? 'rgba(203, 243, 210, 0.30)' : '#F3F3F3',
        borderBottom: isActive ? '3px #006969 solid' : '0.50px #B9B9B9 solid',
        cursor: 'pointer'
    });

    // Process signal quality data based on current view
    const processSignalQualityData = (rawData, view) => {
        if (!rawData || rawData.length === 0) {
            return {
                labels: [],
                datasets: []
            };
        }

        const sortedData = rawData.sort((a, b) => new Date(a.completedDate) - new Date(b.completedDate));
        
        const modalityGroups = {};
        sortedData.forEach(item => {
            const modalityKey = item.modalityType;
            if (!modalityGroups[modalityKey]) {
                modalityGroups[modalityKey] = {
                    modalityType: item.modalityType,
                    data: []
                };
            }
            modalityGroups[modalityKey].data.push(item);
        });

        const modalityColors = [
            { border: '#28a745', background: 'rgba(40, 167, 69, 0.1)', point: '#28a745' },
            { border: '#007bff', background: 'rgba(0, 123, 255, 0.1)', point: '#007bff' },
            { border: '#dc3545', background: 'rgba(220, 53, 69, 0.1)', point: '#dc3545' },
            { border: '#ffc107', background: 'rgba(255, 193, 7, 0.1)', point: '#ffc107' },
            { border: '#6f42c1', background: 'rgba(111, 66, 193, 0.1)', point: '#6f42c1' },
            { border: '#fd7e14', background: 'rgba(253, 126, 20, 0.1)', point: '#fd7e14' },
            { border: '#20c997', background: 'rgba(32, 201, 151, 0.1)', point: '#20c997' },
            { border: '#e83e8c', background: 'rgba(232, 62, 140, 0.1)', point: '#e83e8c' }
        ];
        
        let chartLabels = [];
        const datasets = [];
        
        Object.values(modalityGroups).forEach((modality, index) => {
            const colorIndex = index % modalityColors.length;
            const color = modalityColors[colorIndex];
            
            let chartData = [];
            
            if (view === 'hourly') {
                // For hourly view, show data for the last hour
                const now = new Date();
                const lastHour = new Date(now.getTime() - 60 * 60 * 1000);
                
                // Filter data to last hour
                const recentData = modality.data.filter(item => new Date(item.completedDate) >= lastHour);
                
                // If we have any data, create chart
                if (recentData.length > 0) {
                    // Set labels on first iteration
                    if (index === 0) {
                        chartLabels = recentData.map(item => {
                            const date = new Date(item.completedDate);
                            return date.toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                    });
                        });
                    }
                    
                    // Create data from actual readings
                    chartData = recentData.map(item => {
                        if (modality.modalityType === 'ppg') {
                            // For PPG, use sqiIr if available
                            if (item.sqiIr !== undefined && item.sqiIr !== null) {
                                const sqiIrValue = item.sqiIr < 0 ? 0 : item.sqiIr * 100;
                                return Math.round(sqiIrValue);
                            }
                        } else {
                            // For other modalities, use sqi field
                            if (item.sqi !== undefined && item.sqi !== null) {
                                const sqiValue = item.sqi < 0 ? 0 : item.sqi * 100;
                                return Math.round(sqiValue);
                                }
                            }
                        return null;
                });
                } else {
                    // No data in last hour
                if (index === 0) {
                        chartLabels = [];
                    }
                    chartData = [];
                }
                
            } else if (view === 'daily') {
                const now = new Date();
                const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);
                
                const recentData = modality.data.filter(item => new Date(item.completedDate) >= last24Hours);
                
                const hourlyData = {};
                for (let i = 0; i < 24; i++) {
                    const hour = new Date(now.getTime() - i * 60 * 60 * 1000);
                    const hourKey = hour.toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        hour12: true
                    });
                    hourlyData[hourKey] = {
                        time: hour,
                        signalQualityValues: [],
                        count: 0
                    };
                }
                
                recentData.forEach(item => {
                    const itemTime = new Date(item.completedDate);
                    const hourKey = itemTime.toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        hour12: true
                    });
                    if (hourlyData[hourKey]) {
                        if (modality.modalityType === 'ppg') {
                            if (item.sqiIr !== undefined && item.sqiIr !== null) {
                                const sqiIrValue = item.sqiIr < 0 ? 0 : item.sqiIr * 100;
                                hourlyData[hourKey].signalQualityValues.push(sqiIrValue);
                                if (!hourlyData[hourKey].originalValues) {
                                    hourlyData[hourKey].originalValues = [];
                                }
                                hourlyData[hourKey].originalValues.push(item.sqiIr);
                                hourlyData[hourKey].count++;
                            }
                        } else {
                            if (item.sqi !== undefined && item.sqi !== null) {
                                const sqiValue = item.sqi < 0 ? 0 : item.sqi * 100;
                                hourlyData[hourKey].signalQualityValues.push(sqiValue);
                                if (!hourlyData[hourKey].originalValues) {
                                    hourlyData[hourKey].originalValues = [];
                                }
                                hourlyData[hourKey].originalValues.push(item.sqi);
                                hourlyData[hourKey].count++;
                            }
                        }
                    }
                });
                
                if (index === 0) {
                    chartLabels = Object.keys(hourlyData).reverse();
                }
                chartData = chartLabels.map(label => {
                    const hourData = hourlyData[label];
                    if (hourData && hourData.count > 0) {
                        const averageSignalQuality = hourData.signalQualityValues.reduce((sum, level) => sum + level, 0) / hourData.count;
                        const hasInvalidReadings = false;
                        return {
                            value: Math.round(averageSignalQuality),
                            hasInvalidReadings: hasInvalidReadings
                        };
                    }
                    return null;
                });
            
            } else if (view === 'weekly') {
                const now = new Date();
                const last7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                
                const recentData = modality.data.filter(item => new Date(item.completedDate) >= last7Days);
                
                const dailyData = {};
                for (let i = 0; i < 7; i++) {
                    const day = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
                    const dayKey = day.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                    });
                    dailyData[dayKey] = {
                        date: day,
                        signalQualityValues: [],
                        count: 0
                    };
                }
                
                recentData.forEach(item => {
                    const dateKey = new Date(item.completedDate).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                    });
                    
                    if (dailyData[dateKey]) {
                        if (modality.modalityType === 'ppg') {
                            if (item.sqiIr !== undefined && item.sqiIr !== null) {
                                const sqiIrValue = item.sqiIr < 0 ? 0 : item.sqiIr * 100;
                                dailyData[dateKey].signalQualityValues.push(sqiIrValue);
                                if (!dailyData[dateKey].originalValues) {
                                    dailyData[dateKey].originalValues = [];
                                }
                                dailyData[dateKey].originalValues.push(item.sqiIr);
                                dailyData[dateKey].count++;
                            }
                        } else {
                            if (item.sqi !== undefined && item.sqi !== null) {
                                const sqiValue = item.sqi < 0 ? 0 : item.sqi * 100;
                                dailyData[dateKey].signalQualityValues.push(sqiValue);
                                if (!dailyData[dateKey].originalValues) {
                                    dailyData[dateKey].originalValues = [];
                                }
                                dailyData[dateKey].originalValues.push(item.sqi);
                                dailyData[dateKey].count++;
                            }
                        }
                    }
                });
                
                if (index === 0) {
                    chartLabels = Object.keys(dailyData).reverse();
                }
                chartData = chartLabels.map(label => {
                    const dayData = dailyData[label];
                    if (dayData && dayData.count > 0) {
                        const averageSignalQuality = dayData.signalQualityValues.reduce((sum, level) => sum + level, 0) / dayData.count;
                        const hasInvalidReadings = false;
                        return {
                            value: Math.round(averageSignalQuality),
                            hasInvalidReadings: hasInvalidReadings
                        };
                    }
                    return null;
                });
            
            } else if (view === 'monthly') {
                const now = new Date();
                const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                
                const recentData = modality.data.filter(item => new Date(item.completedDate) >= last30Days);
                
                const dailyData = {};
                for (let i = 0; i < 30; i++) {
                    const day = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
                    const dayKey = day.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                    });
                    dailyData[dayKey] = {
                        date: day,
                        signalQualityValues: [],
                        count: 0
                    };
                }
                
                recentData.forEach(item => {
                    const dateKey = new Date(item.completedDate).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                    });
                    
                    if (dailyData[dateKey]) {
                        if (modality.modalityType === 'ppg') {
                            if (item.sqiIr !== undefined && item.sqiIr !== null) {
                                const sqiIrValue = item.sqiIr < 0 ? 0 : item.sqiIr * 100;
                                dailyData[dateKey].signalQualityValues.push(sqiIrValue);
                                if (!dailyData[dateKey].originalValues) {
                                    dailyData[dateKey].originalValues = [];
                                }
                                dailyData[dateKey].originalValues.push(item.sqiIr);
                                dailyData[dateKey].count++;
                            }
                        } else {
                            if (item.sqi !== undefined && item.sqi !== null) {
                                const sqiValue = item.sqi < 0 ? 0 : item.sqi * 100;
                                dailyData[dateKey].signalQualityValues.push(sqiValue);
                                if (!dailyData[dateKey].originalValues) {
                                    dailyData[dateKey].originalValues = [];
                                }
                                dailyData[dateKey].originalValues.push(item.sqi);
                                dailyData[dateKey].count++;
                            }
                        }
                    }
                });
                
                if (index === 0) {
                    chartLabels = Object.keys(dailyData).reverse();
                }
                chartData = chartLabels.map(label => {
                    const dayData = dailyData[label];
                    if (dayData && dayData.count > 0) {
                        const averageSignalQuality = dayData.signalQualityValues.reduce((sum, level) => sum + level, 0) / dayData.count;
                        const hasInvalidReadings = false;
                        return {
                            value: Math.round(averageSignalQuality),
                            hasInvalidReadings: hasInvalidReadings
                        };
                    }
                    return null;
                });
                
            } else if (view === 'all') {
                if (modality.data.length === 0) {
                    chartData = [];
                } else {
                    const modalitySortedData = modality.data.sort((a, b) => new Date(a.completedDate) - new Date(b.completedDate));
                    const firstDate = new Date(modalitySortedData[0].completedDate);
                    const lastDate = new Date(modalitySortedData[modalitySortedData.length - 1].completedDate);
                    
                    const dailyData = {};
                    const currentDate = new Date(firstDate);
                    while (currentDate <= lastDate) {
                        const dateKey = currentDate.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                        });
                        dailyData[dateKey] = {
                            date: new Date(currentDate),
                            signalQualityValues: [],
                            count: 0
                        };
                        currentDate.setDate(currentDate.getDate() + 1);
                    }
                    
                    modalitySortedData.forEach(item => {
                        const dateKey = new Date(item.completedDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                        });
                        
                        if (dailyData[dateKey]) {
                            if (modality.modalityType === 'ppg') {
                                if (item.sqiIr !== undefined && item.sqiIr !== null) {
                                    const sqiIrValue = item.sqiIr < 0 ? 0 : item.sqiIr * 100;
                                    dailyData[dateKey].signalQualityValues.push(sqiIrValue);
                                    if (!dailyData[dateKey].originalValues) {
                                        dailyData[dateKey].originalValues = [];
                                    }
                                    dailyData[dateKey].originalValues.push(item.sqiIr);
                                    dailyData[dateKey].count++;
                                }
                            } else {
                                if (item.sqi !== undefined && item.sqi !== null) {
                                    const sqiValue = item.sqi < 0 ? 0 : item.sqi * 100;
                                    dailyData[dateKey].signalQualityValues.push(sqiValue);
                                    if (!dailyData[dateKey].originalValues) {
                                        dailyData[dateKey].originalValues = [];
                                    }
                                    dailyData[dateKey].originalValues.push(item.sqi);
                                    dailyData[dateKey].count++;
                                }
                            }
                        }
                    });
                    
                    if (index === 0) {
                        chartLabels = Object.keys(dailyData).sort((a, b) => {
                            return dailyData[a].date - dailyData[b].date;
                        });
                    }
                    
                    chartData = chartLabels.map(label => {
                        const dayData = dailyData[label];
                        if (dayData && dayData.count > 0) {
                            const averageSignalQuality = dayData.signalQualityValues.reduce((sum, level) => sum + level, 0) / dayData.count;
                            return Math.round(averageSignalQuality);
                        }
                        return null;
                    });
                }
            }
            
            if (modality.modalityType === 'bp') {
                return;
            }
            
            const displayName = {
                'ecg': 'ECG',
                'ausc': 'Auscultation', 
                'bioz': 'Bioimpedance',
                'motion': 'Motion',
                'ppg': 'PPG'
            }[modality.modalityType] || modality.modalityType.toUpperCase();
            
            datasets.push({
                label: displayName,
                data: chartData.map(item => item && item.value !== undefined ? item.value : item),
                borderColor: color.border,
                backgroundColor: color.background,
                fill: false,
                tension: 0.4,
                borderWidth: 2,
                pointRadius: 4,
                pointBackgroundColor: color.point,
                metadata: chartData
            });
        });
        
        return {
            labels: chartLabels,
            datasets: datasets
        };
    };

    // Process battery data based on current view
    const processBatteryData = (rawData, view) => {
        if (!rawData || rawData.length === 0) {
            return {
                labels: [],
                datasets: [{
                    label: 'Battery Level',
                    data: [],
                    borderColor: '#1D5769',
                    backgroundColor: 'rgba(29, 87, 105, 0.1)',
                    fill: true,
                    tension: 0.4,
                    borderWidth: 2,
                    pointRadius: 4,
                    pointBackgroundColor: '#1D5769',
                }]
            };
        }

        const sortedData = rawData.sort((a, b) => new Date(a.createdDate) - new Date(b.createdDate));
        
        let chartLabels = [];
        let chartData = [];
        
        if (view === 'hourly') {
            // For hourly view, show data for the last hour
            const now = new Date();
            const lastHour = new Date(now.getTime() - 60 * 60 * 1000);
            
            // Filter data to last hour
            const recentData = sortedData.filter(item => new Date(item.createdDate) >= lastHour);
            
            // If we have any data, create chart
            if (recentData.length > 0) {
                // Create labels and data from actual readings
                chartLabels = recentData.map(item => {
                    const date = new Date(item.createdDate);
                    return date.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                });
                });
                
                chartData = recentData.map(item => Math.round(item.batteryLevel));
            } else {
                // No data in last hour
                chartLabels = [];
                chartData = [];
            }
            
        } else if (view === 'daily') {
            const now = new Date();
            const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);
            
            const recentData = sortedData.filter(item => new Date(item.createdDate) >= last24Hours);
            
            const hourlyData = {};
            for (let i = 0; i < 24; i++) {
                const hour = new Date(now.getTime() - i * 60 * 60 * 1000);
                const hourKey = hour.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    hour12: true
                });
                hourlyData[hourKey] = {
                    time: hour,
                    batteryLevels: [],
                    count: 0
                };
            }
            
            recentData.forEach(item => {
                const itemTime = new Date(item.createdDate);
                const hourKey = itemTime.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    hour12: true
                });
                if (hourlyData[hourKey]) {
                    hourlyData[hourKey].batteryLevels.push(item.batteryLevel);
                    hourlyData[hourKey].count++;
                }
            });
            
            chartLabels = Object.keys(hourlyData).reverse();
            chartData = chartLabels.map(label => {
                const hourData = hourlyData[label];
                if (hourData.count > 0) {
                    const averageBatteryLevel = hourData.batteryLevels.reduce((sum, level) => sum + level, 0) / hourData.count;
                    return Math.round(averageBatteryLevel);
                }
                return null;
            });
            
        } else if (view === 'weekly') {
            const now = new Date();
            const last7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            
            const recentData = sortedData.filter(item => new Date(item.createdDate) >= last7Days);
            
            const dailyData = {};
            for (let i = 0; i < 7; i++) {
                const day = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
                const dayKey = day.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                });
                dailyData[dayKey] = {
                    date: day,
                    batteryLevels: [],
                    count: 0
                };
            }
            
            recentData.forEach(item => {
                const dateKey = new Date(item.createdDate).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                });
                
                if (dailyData[dateKey]) {
                    dailyData[dateKey].batteryLevels.push(item.batteryLevel);
                    dailyData[dateKey].count++;
                }
            });
            
            chartLabels = Object.keys(dailyData).reverse();
            chartData = chartLabels.map(label => {
                const dayData = dailyData[label];
                if (dayData.count > 0) {
                    const averageBatteryLevel = dayData.batteryLevels.reduce((sum, level) => sum + level, 0) / dayData.count;
                    return Math.round(averageBatteryLevel);
                }
                return null;
            });
            
        } else if (view === 'monthly') {
            const now = new Date();
            const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            
            const recentData = sortedData.filter(item => new Date(item.createdDate) >= last30Days);
            
            const dailyData = {};
            for (let i = 0; i < 30; i++) {
                const day = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
                const dayKey = day.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                });
                dailyData[dayKey] = {
                    date: day,
                    batteryLevels: [],
                    count: 0
                };
            }
            
            recentData.forEach(item => {
                const dateKey = new Date(item.createdDate).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                });
                
                if (dailyData[dateKey]) {
                    dailyData[dateKey].batteryLevels.push(item.batteryLevel);
                    dailyData[dateKey].count++;
                }
            });
            
            chartLabels = Object.keys(dailyData).reverse();
            chartData = chartLabels.map(label => {
                const dayData = dailyData[label];
                if (dayData.count > 0) {
                    const averageBatteryLevel = dayData.batteryLevels.reduce((sum, level) => sum + level, 0) / dayData.count;
                    return Math.round(averageBatteryLevel);
                }
                return null;
            });
            
        } else if (view === 'all') {
            if (sortedData.length === 0) {
                chartLabels = [];
                chartData = [];
            } else {
                const firstDate = new Date(sortedData[0].createdDate);
                const lastDate = new Date(sortedData[sortedData.length - 1].createdDate);
                
                const dailyData = {};
                const currentDate = new Date(firstDate);
                while (currentDate <= lastDate) {
                    const dateKey = currentDate.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                    });
                    dailyData[dateKey] = {
                        date: new Date(currentDate),
                        batteryLevels: [],
                        count: 0
                    };
                    currentDate.setDate(currentDate.getDate() + 1);
                }
                
                sortedData.forEach(item => {
                    const dateKey = new Date(item.createdDate).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                    });
                    
                    if (dailyData[dateKey]) {
                        dailyData[dateKey].batteryLevels.push(item.batteryLevel);
                        dailyData[dateKey].count++;
                    }
                });
                
                chartLabels = Object.keys(dailyData).sort((a, b) => {
                    return dailyData[a].date - dailyData[b].date;
                });
                
                chartData = chartLabels.map(label => {
                    const dayData = dailyData[label];
                    if (dayData.count > 0) {
                        const averageBatteryLevel = dayData.batteryLevels.reduce((sum, level) => sum + level, 0) / dayData.count;
                        return Math.round(averageBatteryLevel);
                    }
                    return null;
                });
            }
        }
        
        return {
            labels: chartLabels,
            datasets: [{
                label: 'Battery Level',
                data: chartData,
                borderColor: '#1D5769',
                backgroundColor: 'rgba(29, 87, 105, 0.1)',
                fill: true,
                tension: 0.4,
                borderWidth: 2,
                pointRadius: 4,
                pointBackgroundColor: '#1D5769',
            }]
        };
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        } catch (error) {
            return 'Invalid Date';
        }
    };

    // Format time ago for last reboot
    const formatTimeAgo = (timestamp) => {
        if (!timestamp) return 'N/A';
        
        try {
            const now = new Date();
            const rebootTime = new Date(timestamp);
            const diffMs = now - rebootTime;
            const diffMinutes = Math.floor(diffMs / (1000 * 60));
            
            if (diffMinutes < 60) {
                return `${diffMinutes}m ago`;
            } else if (diffMinutes < 1440) { // Less than 24 hours
                const hours = Math.floor(diffMinutes / 60);
                return `${hours}h ago`;
            } else { // Days
                const days = Math.floor(diffMinutes / 1440);
                return `${days}d ago`;
            }
        } catch (e) {
            return 'N/A';
        }
    };




    const getLatestBatteryReading = () => {
        if (!rawBatteryData || rawBatteryData.length === 0) {
            return null;
        }
        
        const sortedData = [...rawBatteryData].sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
        return sortedData[0];
    };

    // Get overall test status from test results
    const getOverallTestStatus = () => {
        if (!selfTestResults || !selfTestResults.testResults) {
            return { status: 'Unknown', color: '#6c757d' };
        }
        
        const allPassed = selfTestResults.testResults.every(test => test.test_status === 1);
        return {
            status: allPassed ? 'Passed' : 'Failed',
            color: allPassed ? '#28a745' : '#dc3545'
        };
    };

    // Get test status color
    const getTestStatusColor = (testStatus) => {
        return testStatus === 1 ? '#28a745' : '#dc3545';
    };

    // Format test name for display
    const formatTestName = (testName, testCode) => {
        const nameMap = {
            'Mem': 'Memory Check',
            'Sen': 'Sensor Calibration',
            'Com': 'Communication Test',
            'Bat': 'Battery Test',
            'Sig': 'Signal Test',
            'AUDIO': 'Audio Check'
        };
        const displayName = testName || testCode || 'Unknown Test';
        return nameMap[displayName] || displayName;
    };

    // Helper function for status colors
    const getStatusColor = (status) => {
        if (!status) return '#6c757d';
        switch (status.toLowerCase()) {
            case 'normal':
            case 'active':
                return '#28a745';
            case 'warning':
                return '#ffc107';
            case 'error':
            case 'critical':
                return '#dc3545';
            case 'low':
                return '#ffc107';
            case 'medium':
                return '#fd7e14';
            case 'high':
                return '#dc3545';
            default:
                return '#6c757d';
        }
    };

    // Render modality tracking status indicator
    const renderModalityStatus = (scheduleKey) => {
        const status = modalityTrackingStatus[scheduleKey];
        
        if (!status || !status.hasData) {
            return (
                <span style={{
                    display: 'inline-block',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#6c757d',
                    marginRight: '8px',
                    verticalAlign: 'middle'
                }} title="Insufficient data" />
            );
        }

        const color = status.isOnTrack ? '#28a745' : '#dc3545';
        const title = status.isOnTrack 
            ? `On Track (${status.timeDelta.toFixed(1)} mins between readings)`
            : `Off Track (${status.timeDelta.toFixed(1)} mins between readings, expected ${status.frequency} mins)`;

        return (
            <span style={{
                display: 'inline-block',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: color,
                marginRight: '8px',
                verticalAlign: 'middle',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }} title={title} />
        );
    };

    // Chart data configuration
    const batteryHistoryData = batteryData || {
        labels: [],
        datasets: [
            {
                label: 'Battery Level',
                data: [],
                borderColor: '#1D5769',
                backgroundColor: 'rgba(29, 87, 105, 0.1)',
                fill: true,
                tension: 0.4,
                borderWidth: 2,
                pointRadius: 4,
                pointBackgroundColor: '#1D5769',
            }
        ]
    };

    const signalQualityHistoryData = signalQualityData || {
        labels: [],
        datasets: [
            {
                label: 'Signal Quality',
                data: [],
                borderColor: '#28a745',
                backgroundColor: 'rgba(40, 167, 69, 0.1)',
                fill: true,
                tension: 0.4,
                borderWidth: 2,
                pointRadius: 4,
                pointBackgroundColor: '#28a745',
            }
        ]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                top: 20,
                bottom: 20,
                left: 10,
                right: 10
            }
        },
        plugins: {
            legend: {
                display: true,
                position: 'top',
                labels: {
                    font: {
                        size: 14,
                        weight: 'bold'
                    },
                    padding: 20
                },
                padding: {
                    top: 60,
                    bottom: 20
                }
            },
            tooltip: {
                mode: 'index',
                intersect: false,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                titleColor: '#000',
                bodyColor: '#000',
                borderColor: '#ddd',
                borderWidth: 1,
                padding: 12,
                titleFont: {
                    size: 14,
                    weight: 'bold'
                },
                bodyFont: {
                    size: 13
                },
                callbacks: {
                    label: function(context) {
                        if (context.dataset.label.includes('ECG') || context.dataset.label.includes('Signal Quality') || 
                            context.dataset.label.includes('Auscultation') || context.dataset.label.includes('Blood Pressure') ||
                            context.dataset.label.includes('Bioimpedance') || context.dataset.label.includes('Motion') ||
                            context.dataset.label.includes('PPG')) {
                            
                            if (context.dataset.metadata && context.dataset.metadata[context.dataIndex]) {
                                const metadata = context.dataset.metadata[context.dataIndex];
                                if (metadata && metadata.hasInvalidReadings) {
                                    return `${context.dataset.label}: Invalid Reading`;
                                }
                            }
                            return `${context.dataset.label}: ${Math.round(context.parsed.y)}%`;
                        }
                        return `${context.dataset.label}: ${context.parsed.y}%`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                max: 100,
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)',
                    drawBorder: false
                },
                ticks: {
                    callback: value => `${value}%`,
                    font: {
                        size: 12
                    },
                    padding: 15
                },
                offset: true
            },
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    font: {
                        size: 12
                    },
                    padding: 8
                }
            }
        }
    };

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
        p: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
    };

    return (
        <>
            {/* Tab Navigation */}
            <div style={{width: '100%', borderBottom: '0.50px #B9B9B9 solid', marginBottom: '20px'}}>
                <div style={{height: 32, display: 'flex', justifyContent: 'flex-start'}}>
                    <div 
                        style={tabStyle(activeTab === 'monitoring')}
                        onClick={() => setActiveTab('monitoring')}
                    >
                        <span style={{
                            color: activeTab === 'monitoring' ? 'black' : '#373737',
                            fontSize: 16,
                            fontFamily: 'Roboto',
                            fontWeight: '400',
                            lineHeight: '24px',
                            letterSpacing: 0.04
                        }}>Device Monitoring</span>
                    </div>
                    <div 
                        style={tabStyle(activeTab === 'details')}
                        onClick={() => setActiveTab('details')}
                    >
                        <span style={{
                            color: activeTab === 'details' ? 'black' : '#373737',
                            fontSize: 16,
                            fontFamily: 'Roboto',
                            fontWeight: '400',
                            lineHeight: '24px',
                            letterSpacing: 0.04
                        }}>Device Details</span>
                    </div>
                </div>
            </div>

            {/* Device Details Tab Content */}
            {activeTab === 'details' && (
            <div style={{padding: '24px', background: 'white', boxShadow: '0px 20px 24.2px rgba(0, 0, 0, 0.06)', borderRadius: '16px', border: '0.8px solid #D6D6D6'}}>
                {/* Header Section */}
                <div style={{borderBottom: '0.5px solid #9D9D9E', paddingBottom: '10px', marginBottom: '20px'}}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <h1 style={{fontSize: '32px', fontFamily: 'DM Serif Display', marginBottom: '10px'}}>
                            Health Patch - {deviceData?.patientName || deviceData?.firstName + " " + deviceData?.lastName || 'Unassigned'}
                        </h1>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            {deviceData?.patientId && deviceData.patientId !== "-" && (
                                <>
                                    <button
                                        onClick={() => handleAction('self-test')}
                                        disabled={processingAction === 'self-test'}
                                        style={{
                                            backgroundColor: processingAction === 'self-test' ? '#ccc' : '#CA3B3B',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: '8px 16px',
                                            cursor: processingAction === 'self-test' ? 'not-allowed' : 'pointer',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            height: '36px',
                                            marginTop: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        {processingAction === 'self-test' && (
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                        )}
                                        Device Self-Test
                                    </button>
                                    <button
                                        onClick={() => handleAction('battery')}
                                        disabled={processingAction === 'battery'}
                                        style={{
                                            backgroundColor: processingAction === 'battery' ? '#ccc' : '#ffc107',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: '8px 16px',
                                            cursor: processingAction === 'battery' ? 'not-allowed' : 'pointer',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            height: '36px',
                                            marginTop: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        {processingAction === 'battery' && (
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                        )}
                                        Request Batteries
                                    </button>
                                    <button
                                        onClick={() => handleAction('firmware')}
                                        disabled={processingAction === 'firmware'}
                                        style={{
                                            backgroundColor: processingAction === 'firmware' ? '#ccc' : '#dc3545',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: '8px 16px',
                                            cursor: processingAction === 'firmware' ? 'not-allowed' : 'pointer',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            height: '36px',
                                            marginTop: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        {processingAction === 'firmware' && (
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                        )}
                                        Firmware Update
                                    </button>
                                </>
                            )}
                            {deviceData?.checkInOutStatus === 'Checked Out' ? (
                                <button
                                    onClick={handleCheckin}
                                    style={{
                                        backgroundColor: '#006969',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '8px 16px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        height: '36px',
                                        marginTop: '4px'
                                    }}
                                >
                                    Check In Device
                                </button>
                            ) : (
                                <button
                                    onClick={() => setShowCheckoutModal(true)}
                                    style={{
                                        backgroundColor: '#006969',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '8px 16px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        height: '36px',
                                        marginTop: '4px'
                                    }}
                                >
                                    Check Out Device
                                </button>
                            )}
                        </div>
                    </div>
                    <div>
                        {deviceData?.isRefurbished && 
                            <span style={{color: '#30584A', fontSize: '16px', fontWeight: '500', marginRight: '40px'}}>✓ Refurbished</span>
                        }
                        <span style={{color: '#4C5793', fontSize: '16px', fontWeight: '500', marginLeft: '0px'}}>
                            Serial Number: {(deviceData?.serialNumber && deviceData.serialNumber !== 'string') ? deviceData.serialNumber : 'N/A'} 
                            <span style={{margin: '0 20px'}}></span>
                            Mac Address: {deviceData?.macAddress || 'Unknown'}
                            <span style={{margin: '0 20px'}}></span>
                            Reboot Count: {rebootCount !== null ? rebootCount : 'N/A'}
                            <span style={{margin: '0 20px'}}></span>
                            Last Reboot: {formatTimeAgo(lastRebootTimestamp)}
                        </span>
                    </div>
                </div>

                {/* Two Column Layout */}
                <div style={{display: 'flex', gap: '30px', alignItems: 'flex-start'}}>
                    {/* Left Column */}
                    <div style={{flex: '1'}}>
                        {/* Manufacturing Information Section */}
                        <div style={{marginBottom: '30px'}}>
                    <h2 style={{fontSize: '18px', fontWeight: '800', marginBottom: '15px'}}>Manufacturing Information</h2>
                    <div style={{display: 'grid', gap: '12px'}}>
                        <div style={{display: 'flex', gap: '12px'}}>
                            <span style={{fontWeight: '500'}}>Hardware Version: {patchMetadata?.deviceSoftwareVersion || deviceData?.hardwareVersion || 'N/A'}</span>
                        </div>
                        <div style={{display: 'flex', gap: '12px'}}>
                            <span style={{fontWeight: '500'}}>Software Version: {(patchMetadata?.appSoftwareVersion && patchMetadata.appSoftwareVersion !== 'string') ? patchMetadata.appSoftwareVersion : (deviceData?.currentSoftwareVersion && deviceData.currentSoftwareVersion !== 'string' ? deviceData.currentSoftwareVersion : 'N/A')}</span>
                        </div>
                        <div style={{display: 'flex', gap: '12px'}}>
                            <span style={{fontWeight: '500'}}>Housing Version: {deviceData?.housingVersion || 'N/A'}</span>
                        </div>

                        <div style={{display: 'flex', gap: '20px', flexWrap: 'wrap'}}>
                            <div style={{display: 'flex', gap: '12px'}}>
                                <span style={{fontWeight: '500'}}>Electronics Manufacturer: {deviceData?.electronicsManufacturerName || 'N/A'}</span>
                            </div>
                            <div style={{display: 'flex', gap: '12px'}}>
                                <span style={{fontWeight: '500'}}>Date: {deviceData?.electronicsManufacturerDate ? 
                                    formatDateString(deviceData.electronicsManufacturerDate, userDateFormat) : 'N/A'}</span>
                            </div>
                            <div style={{display: 'flex', gap: '12px'}}>
                                <span style={{fontWeight: '500'}}>Batch #: {deviceData?.electronicsManufacturerBatch || 'N/A'}</span>
                            </div>
                        </div>

                        <div style={{display: 'flex', gap: '20px', flexWrap: 'wrap'}}>
                            <div style={{display: 'flex', gap: '12px'}}>
                                <span style={{fontWeight: '500'}}>Plastics Manufacturer: {deviceData?.plasticsManufacturerName || 'N/A'}</span>
                            </div>
                            <div style={{display: 'flex', gap: '12px'}}>
                                <span style={{fontWeight: '500'}}>Date: {deviceData?.plasticsManufacturerDate ? 
                                    formatDateString(deviceData.plasticsManufacturerDate, userDateFormat) : 'N/A'}</span>
                            </div>
                            <div style={{display: 'flex', gap: '12px'}}>
                                <span style={{fontWeight: '500'}}>Batch #: {deviceData?.plasticsManufacturerBatch || 'N/A'}</span>
                            </div>
                        </div>
                        <div style={{display: 'flex', gap: '20px', flexWrap: 'wrap'}}>
                            <div style={{display: 'flex', gap: '12px'}}>
                                <span style={{fontWeight: '500'}}>Assembly Contract Manufacturer: {deviceData?.assemblyManufacturerName || 'N/A'}</span>
                            </div>
                            <div style={{display: 'flex', gap: '12px'}}>
                                <span style={{fontWeight: '500'}}>Date: {deviceData?.assemblyManufacturerDate ? 
                                    formatDateString(deviceData.assemblyManufacturerDate, userDateFormat) : 'N/A'}</span>
                            </div>
                            <div style={{display: 'flex', gap: '12px'}}>
                                <span style={{fontWeight: '500'}}>Batch #: {deviceData?.assemblyManufacturerBatch || 'N/A'}</span>
                            </div>
                        </div>

                        <div style={{display: 'flex', gap: '12px'}}>
                            <span style={{fontWeight: '500'}}>Initial Testing Date: {deviceData?.initialTestingDate ? 
                                formatDateString(deviceData.initialTestingDate, userDateFormat) : 'N/A'}</span>
                        </div>
                    </div>
                </div>

                {/* Device Information Section */}
                <div style={{marginBottom: '30px'}}>
                    <h2 style={{fontSize: '18px', fontWeight: '800', marginBottom: '15px'}}>Device Information</h2>
                    <div style={{display: 'grid', gap: '12px'}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                            <span style={{fontWeight: '500'}}>Device Status: 
                                <div style={{
                                    display: 'inline-block',
                                    width: '18px', 
                                    height: '18px', 
                                    background: deviceData?.isActive ? '#2A7F38' : '#FF0000', 
                                    borderRadius: '50%',
                                    margin: '0 8px',
                                    verticalAlign: 'middle'
                                }}></div>
                                {deviceData?.isActive ? 'Active' : 'Inactive'}
                            </span>
                        </div>
                        <div style={{display: 'flex', gap: '12px'}}>
                            <span style={{fontWeight: '500'}}>Date Assigned: {deviceData?.dateAssigned ? 
                                formatDateString(deviceData.dateAssigned, userDateFormat) : 'N/A'}</span>
                        </div>
                        <div style={{display: 'flex', gap: '12px'}}>
                            <span style={{fontWeight: '500'}}>Last Connection: {patchMetadata?.createdDate ? 
                                formatDate(patchMetadata.createdDate) : (deviceData?.lastConnection ? 
                                formatDateString(deviceData.lastConnection, userDateFormat) : 'N/A')}</span>
                        </div>
                        <div style={{display: 'flex', gap: '12px'}}>
                            <span style={{fontWeight: '500'}}>Last Data Transmission Time: {deviceReadings.length > 0 ? (() => {
                                const sortedReadings = [...deviceReadings].sort((a, b) => 
                                    new Date(b.completedDate) - new Date(a.completedDate)
                                );
                                return formatDate(sortedReadings[0].completedDate);
                            })() : (latestRecordingTimestamp ? 
                                formatDate(latestRecordingTimestamp) : (deviceData?.lastDataTransmissionTime ? 
                                formatDateString(deviceData.lastDataTransmissionTime, userDateFormat) : 'N/A'))}</span>
                        </div>
                        <div style={{display: 'flex', gap: '12px'}}>
                            <span style={{fontWeight: '500'}}>Free Memory Size: {patchMetadata?.freeMemorySize ? `${(patchMetadata.freeMemorySize / (1024 * 1024)).toFixed(2)} MB` : 'N/A'}</span>
                        </div>
                        {patchMetadata?.deviceSchedule && (
                            <div style={{marginTop: '20px'}}>
                                <h3 style={{fontSize: '16px', fontWeight: '600', marginBottom: '10px'}}>Device Schedule</h3>
                                <div style={{display: 'grid', gap: '8px', marginLeft: '20px'}}>
                                    <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                                        {renderModalityStatus('ecgMins')}
                                        <span style={{fontWeight: '500'}}>
                                            ECG: {patchMetadata.deviceSchedule.ecgMins} mins
                                            {modalityLastTransmission.ecg && (
                                                <span style={{color: '#666', fontSize: '0.9em', marginLeft: '8px'}}>
                                                    (Last: {formatTimeAgo(modalityLastTransmission.ecg)})
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                                        {renderModalityStatus('ppgMins')}
                                        <span style={{fontWeight: '500'}}>
                                            PPG: {patchMetadata.deviceSchedule.ppgMins} mins
                                            {modalityLastTransmission.ppg && (
                                                <span style={{color: '#666', fontSize: '0.9em', marginLeft: '8px'}}>
                                                    (Last: {formatTimeAgo(modalityLastTransmission.ppg)})
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                                        {renderModalityStatus('audioMins')}
                                        <span style={{fontWeight: '500'}}>
                                            Audio: {patchMetadata.deviceSchedule.audioMins} mins
                                            {modalityLastTransmission.ausc && (
                                                <span style={{color: '#666', fontSize: '0.9em', marginLeft: '8px'}}>
                                                    (Last: {formatTimeAgo(modalityLastTransmission.ausc)})
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                                        {renderModalityStatus('tempMins')}
                                        <span style={{fontWeight: '500'}}>
                                            Temperature: {patchMetadata.deviceSchedule.tempMins} mins
                                            {modalityLastTransmission.temp && (
                                                <span style={{color: '#666', fontSize: '0.9em', marginLeft: '8px'}}>
                                                    (Last: {formatTimeAgo(modalityLastTransmission.temp)})
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                                        {renderModalityStatus('biozMins')}
                                        <span style={{fontWeight: '500'}}>
                                            Bioimpedance: {patchMetadata.deviceSchedule.biozMins} mins
                                            {modalityLastTransmission.bioz && (
                                                <span style={{color: '#666', fontSize: '0.9em', marginLeft: '8px'}}>
                                                    (Last: {formatTimeAgo(modalityLastTransmission.bioz)})
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Clinical Study Information Section */}
                <div style={{marginBottom: '30px'}}>
                    <h2 style={{fontSize: '18px', fontWeight: '800', marginBottom: '15px'}}>Clinical Study Information</h2>
                    {clinicalInfo && (clinicalInfo.studyId || clinicalInfo.studySite || clinicalInfo.collectionInfo) ? (
                        <div style={{display: 'grid', gap: '12px', marginBottom: '20px'}}>
                            <div style={{display: 'flex', gap: '12px'}}>
                                <span style={{fontWeight: '500'}}>Study ID: {clinicalInfo.studyId || '-'}</span>
                            </div>
                            <div style={{display: 'flex', gap: '12px'}}>
                                <span style={{fontWeight: '500'}}>Study Site: {clinicalInfo.studySite || '-'}</span>
                            </div>
                            <div style={{display: 'flex', gap: '12px'}}>
                                <span style={{fontWeight: '500'}}>Collection Info: {clinicalInfo.collectionInfo || '-'}</span>
                            </div>
                        </div>
                    ) : (
                        <p style={{color: '#666', fontStyle: 'italic', marginBottom: '20px'}}>No clinical study information available</p>
                    )}
                    {/* Clinical Study Button */}
                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <button
                            onClick={handleOpenModal}
                            style={{
                                backgroundColor: 'transparent',
                                color: '#006969',
                                border: '1px solid #006969',
                                borderRadius: '4px',
                                padding: '6px 12px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '400'
                            }}
                        >
                            {clinicalInfo && (clinicalInfo.studyId || clinicalInfo.studySite || clinicalInfo.collectionInfo) 
                                ? 'Update Clinical Study Information' 
                                : 'Assign to a Clinical Study'}
                        </button>
                    </div>
                </div>

                {deviceData?.checkInOutStatus === 'Checked Out' && (
                    <div>
                        <h2 style={{fontSize: '18px', fontWeight: '800', marginBottom: '15px'}}>In-House Catalog</h2>
                        <div style={{display: 'grid', gap: '12px'}}>
                            <div style={{display: 'flex', gap: '12px'}}>
                                <span style={{fontWeight: '500'}}>Check Out Status: 
                                    <span style={{
                                        color: '#FF4444',
                                        marginLeft: '8px'
                                    }}>
                                        Checked Out
                                    </span>
                                </span>
                            </div>
                            <div style={{display: 'flex', gap: '12px'}}>
                                <span style={{fontWeight: '500'}}>Team Member Assigned: {deviceData?.teamMemberAssigned || '-'}</span>
                            </div>
                            <div style={{display: 'flex', gap: '12px'}}>
                                <span style={{fontWeight: '500'}}>Check Out Reason: {deviceData?.checkOutReason || '-'}</span>
                            </div>
                            <div style={{display: 'flex', gap: '12px'}}>
                                <span style={{fontWeight: '500'}}>Software: {deviceData?.checkOutSoftware || '-'}</span>
                            </div>
                            <div style={{display: 'flex', gap: '12px'}}>
                                <span style={{fontWeight: '500'}}>Checkout Time: {deviceData?.checkOutDateTime ? 
                                    formatDateString(deviceData.checkOutDateTime, userDateFormat) : '-'}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Device Notes Section */}
                <div style={{marginBottom: '30px', marginTop: '30px'}}>
                    <h2 style={{fontSize: '18px', fontWeight: '800', marginBottom: '15px'}}>Device Notes</h2>
                    <div style={{display: 'grid', gap: '12px', marginBottom: '20px'}}>
                        {deviceNotes.length > 0 ? (
                            deviceNotes.map((note, index) => (
                                <div key={index} style={{
                                    padding: '12px',
                                    backgroundColor: '#F5F5F5',
                                    borderRadius: '4px',
                                    border: '1px solid #E0E0E0'
                                }}>
                                    <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px'}}>
                                        <span style={{fontSize: '12px', color: '#666'}}>
                                            {formatDateString(note.timestamp, userDateFormat)}
                                        </span>
                                    </div>
                                    <p style={{margin: 0, fontSize: '14px'}}>{note.note}</p>
                                </div>
                            ))
                        ) : (
                            <p style={{color: '#666', fontStyle: 'italic', marginBottom: '20px'}}>No device notes available</p>
                        )}
                    </div>
                    {/* Add Notes Button */}
                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <button
                            onClick={() => setShowNotesModal(true)}
                            style={{
                                backgroundColor: 'transparent',
                                color: '#006969',
                                border: '1px solid #006969',
                                borderRadius: '4px',
                                padding: '6px 12px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '400'
                            }}
                        >
                            Add Device Note
                        </button>
                    </div>
                </div>

                {/* Add Checkout Log Section */}
                <div style={{marginBottom: '30px'}}>
                    <h2 style={{fontSize: '18px', fontWeight: '800', marginBottom: '15px'}}>Device Checkout History</h2>
                    <div style={{display: 'grid', gap: '12px', marginBottom: '20px'}}>
                        {checkoutLogData.length > 0 ? (
                            <div style={{
                                border: '1px solid #E0E0E0',
                                borderRadius: '4px',
                                overflow: 'hidden'
                            }}>
                                <table style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                    backgroundColor: 'white'
                                }}>
                                    <thead>
                                        <tr style={{
                                            backgroundColor: '#F5F5F5',
                                            borderBottom: '1px solid #E0E0E0'
                                        }}>
                                            <th style={{padding: '12px', textAlign: 'left', fontWeight: '600'}}>Status</th>
                                            <th style={{padding: '12px', textAlign: 'left', fontWeight: '600'}}>Name</th>
                                            <th style={{padding: '12px', textAlign: 'left', fontWeight: '600'}}>Reason</th>
                                            <th style={{padding: '12px', textAlign: 'left', fontWeight: '600'}}>Software</th>
                                            <th style={{padding: '12px', textAlign: 'left', fontWeight: '600'}}>Check Out Date</th>
                                            <th style={{padding: '12px', textAlign: 'left', fontWeight: '600'}}>Check In Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {checkoutLogData.map((log, index) => (
                                            <tr key={index} style={{
                                                borderBottom: '1px solid #E0E0E0'
                                            }}>
                                                <td style={{padding: '12px'}}>
                                                    <span style={{
                                                        color: log.status === 'Checked Out' ? '#FF4444' : '#006969',
                                                        fontWeight: '500'
                                                    }}>
                                                        {log.status}
                                                    </span>
                                                </td>
                                                <td style={{padding: '12px'}}>{log.userName || '-'}</td>
                                                <td style={{padding: '12px'}}>{log.reason || '-'}</td>
                                                <td style={{padding: '12px'}}>{log.software || '-'}</td>
                                                <td style={{padding: '12px'}}>{log.checkoutDate}</td>
                                                <td style={{padding: '12px'}}>{log.checkinDate}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p style={{color: '#666', fontStyle: 'italic'}}>No checkout history available</p>
                        )}
                    </div>
                </div>
                    </div>

                    {/* Right Column */}
                    <div style={{flex: '1', minWidth: '300px'}}>
                        {/* Action History Section */}
                        <div style={{marginBottom: '30px'}}>
                            <h2 style={{fontSize: '18px', fontWeight: '800', marginBottom: '15px'}}>Action History</h2>
                            <div style={{display: 'grid', gap: '12px', marginBottom: '20px'}}>
                                {loadingActions ? (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </Spinner>
                                    </div>
                                ) : actionLogs.length > 0 ? (
                                    <div style={{
                                        border: '1px solid #E0E0E0',
                                        borderRadius: '4px',
                                        overflow: 'hidden'
                                    }}>
                                        <table style={{
                                            width: '100%',
                                            borderCollapse: 'collapse',
                                            backgroundColor: 'white'
                                        }}>
                                            <thead>
                                                <tr style={{
                                                    backgroundColor: '#F5F5F5',
                                                    borderBottom: '1px solid #E0E0E0'
                                                }}>
                                                    <th style={{padding: '12px', textAlign: 'left', fontWeight: '600'}}>Action</th>
                                                    <th style={{padding: '12px', textAlign: 'left', fontWeight: '600'}}>Date & Time</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {actionLogs.map((log, index) => (
                                                    <tr key={index} style={{
                                                        borderBottom: index < actionLogs.length - 1 ? '1px solid #E0E0E0' : 'none'
                                                    }}>
                                                        <td style={{padding: '12px'}}>
                                                            <span style={{
                                                                color: '#1D5769',
                                                                fontWeight: '500'
                                                            }}>
                                                                {log.deviceControl}
                                                            </span>
                                                        </td>
                                                        <td style={{padding: '12px'}}>
                                                            {formatDate(log.createdDate)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <p style={{color: '#666', fontStyle: 'italic'}}>No actions available</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            )}

            {/* Device Monitoring Tab Content */}
            {activeTab === 'monitoring' && (
                <div style={{padding: '24px', background: 'white', boxShadow: '0px 20px 24.2px rgba(0, 0, 0, 0.06)', borderRadius: '16px', border: '0.8px solid #D6D6D6'}}>
                    <div style={{marginBottom: '20px'}}>
                        <h3 className="mb-2">
                            Health Patch
                            <span className="ms-3">
                                <span style={{
                                    display: 'inline-block',
                                    width: '16px',
                                    height: '16px',
                                    borderRadius: '50%',
                                    backgroundColor: isDeviceOnTrack === true ? getStatusColor('Active') : isDeviceOnTrack === false ? getStatusColor('Inactive') : '#6c757d',
                                    marginRight: '8px',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }} />
                                <span style={{ fontSize: '0.7em', fontWeight: 'bold' }}>
                                    {isDeviceOnTrack === true ? 'On Track' : isDeviceOnTrack === false ? 'Off Track' : 'No Data'}
                                </span>
                            </span>
                        </h3>
                        <div>
                            <span className="me-3"><strong>Serial Number:</strong> {(patchMetadata?.serialNumber && patchMetadata.serialNumber !== 'string') ? patchMetadata.serialNumber : (deviceData?.serialNumber && deviceData.serialNumber !== 'string' ? deviceData.serialNumber : 'N/A')}</span>
                            <span className="me-3"><strong>Mac Address:</strong> {deviceData?.macAddress ?? "Unknown"}</span>
                            <span className="me-3"><strong>Reboot Count:</strong> {rebootCount !== null ? rebootCount : 'N/A'}</span>
                            <span><strong>Last Reboot:</strong> {formatTimeAgo(lastRebootTimestamp)}</span>
                        </div>
                        <div className="mt-2">
                            <span className="me-3">
                                <strong>Last Connection: </strong>{patchMetadata?.createdDate ? formatDate(patchMetadata.createdDate) : formatDate(latestRecordingTimestamp || deviceData?.lastConnection)}
                            </span>
                            <span className="me-3">
                                <strong>Last Data Transmission: </strong>{deviceReadings.length > 0 ? (() => {
                                    const sortedReadings = [...deviceReadings].sort((a, b) => 
                                        new Date(b.completedDate) - new Date(a.completedDate)
                                    );
                                    return formatDate(sortedReadings[0].completedDate);
                                })() : formatDate((deviceData?.lastDataTransmissionTime ? deviceData.lastDataTransmissionTime : null) || latestRecordingTimestamp)}
                            </span>
                            <span>
                                <strong>Software Version: </strong>{(patchMetadata?.appSoftwareVersion && patchMetadata.appSoftwareVersion !== 'string') ? patchMetadata.appSoftwareVersion : (deviceData?.currentSoftwareVersion && deviceData.currentSoftwareVersion !== 'string' ? deviceData.currentSoftwareVersion : 'N/A')}
                            </span>
                        </div>
                    </div>

                    <Row style={{width: '100%'}}>
                        <Col lg={5}>
                            {/* Battery Status */}
                            <Card className="mb-4 shadow-sm">
                                <Card.Body>
                                    <Card.Title className="mb-3">Battery Status</Card.Title>
                                    {(() => {
                                        const latestBatteryReading = getLatestBatteryReading();
                                        
                                        if (!latestBatteryReading) {
                                            return (
                                                <div className="d-flex align-items-center mb-2">
                                                    <div style={{
                                                        width: '100%',
                                                        height: '24px',
                                                        backgroundColor: '#e9ecef',
                                                        borderRadius: '12px',
                                                        overflow: 'hidden',
                                                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
                                                    }}>
                                                        <div style={{
                                                            width: '0%',
                                                            height: '100%',
                                                            backgroundColor: '#6c757d',
                                                            transition: 'width 0.3s ease',
                                                            boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.2)'
                                                        }} />
                                                    </div>
                                                    <span className="ms-3 fw-bold" style={{ 
                                                        color: '#6c757d',
                                                        minWidth: '100px'
                                                    }}>
                                                        No Data
                                                    </span>
                                                </div>
                                            );
                                        }

                                        const batteryLevel = latestBatteryReading.batteryLevel;
                                        const statusText = getBatteryStatusText(batteryLevel);
                                        
                                        return (
                                            <div className="d-flex align-items-center mb-2">
                                                <div style={{
                                                    width: '100%',
                                                    height: '24px',
                                                    backgroundColor: '#e9ecef',
                                                    borderRadius: '12px',
                                                    overflow: 'hidden',
                                                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
                                                }}>
                                                    <div style={{
                                                        width: `${batteryLevel}%`,
                                                        height: '100%',
                                                        backgroundColor: getBatteryColor(batteryLevel),
                                                        transition: 'width 0.3s ease',
                                                        boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.2)'
                                                    }} />
                                                </div>
                                                <span className="ms-3 fw-bold" style={{ 
                                                    color: getBatteryColor(batteryLevel),
                                                    minWidth: '100px'
                                                }}>
                                                    {batteryLevel}% - {statusText}
                                                </span>
                                            </div>
                                        );
                                    })()}
                                </Card.Body>
                            </Card>

                            {/* Active Errors */}
                            <Card className="mb-4 shadow-sm">
                                <Card.Body>
                                    <Card.Title className="mb-3">Active Errors</Card.Title>
                                    {alertsData && alertsData.length > 0 ? (
                                        (() => {
                                            // Helper function to get the correct timestamp for an alert
                                            const getAlertTimestamp = (alert) => {
                                                if (alert.alert_title.includes("Bluetooth")) {
                                                    return alert.createdDate;
                                                } else if (alert.alert_name === 'Lead Off') {
                                                    return alert.alert_lastUpdatedTimestamp;
                                                } else {
                                                    return alert.alert_lastUpdatedTimestamp;
                                                }
                                            };

                                            // Group alerts by alert_name
                                            const groupedAlerts = alertsData.reduce((acc, alert) => {
                                                const alertName = alert.alert_display_name_business || alert.alert_title || alert.alert_name || 'Unknown Alert';
                                                if (!acc[alertName]) {
                                                    acc[alertName] = [];
                                                }
                                                acc[alertName].push(alert);
                                                return acc;
                                            }, {});

                                            // Sort groups by the latest alert date (most recent groups first)
                                            const sortedGroups = Object.entries(groupedAlerts).sort(([, alertsA], [, alertsB]) => {
                                                const latestA = Math.max(...alertsA.map(a => {
                                                    const timestamp = getAlertTimestamp(a);
                                                    return new Date(timestamp || a.alert_lastUpdatedTimestamp).getTime();
                                                }));
                                                const latestB = Math.max(...alertsB.map(a => {
                                                    const timestamp = getAlertTimestamp(a);
                                                    return new Date(timestamp || a.alert_lastUpdatedTimestamp).getTime();
                                                }));
                                                return latestB - latestA;
                                            });

                                            // Render each group
                                            return sortedGroups.map(([alertName, alerts]) => {
                                                // Sort alerts by date descending (latest first)
                                                const sortedAlerts = alerts.sort((a, b) => {
                                                    const timestampA = getAlertTimestamp(a);
                                                    const timestampB = getAlertTimestamp(b);
                                                    return new Date(timestampB) - new Date(timestampA);
                                                });
                                                const latestAlert = sortedAlerts[0];
                                                const hasMultiple = sortedAlerts.length > 1;
                                                const groupKey = `group-${alertName}`;
                                                const isGroupExpanded = expandedAlerts[groupKey];
                                                
                                                return (
                                                    <div key={groupKey} className="mb-4">
                                                        <div className="d-flex align-items-center mb-2">
                                                            <span style={{
                                                                display: 'inline-block',
                                                                width: '16px',
                                                                height: '16px',
                                                                borderRadius: '50%',
                                                                backgroundColor: getStatusColor(latestAlert.alert_priority),
                                                                marginRight: '12px',
                                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                            }} />
                                                            <strong className="me-2">{alertName}</strong>
                                                            {hasMultiple && (
                                                                <span className="badge bg-secondary me-2">{sortedAlerts.length}</span>
                                                            )}
                                                            <span className="text-muted" style={{ fontSize: '0.9em' }}>
                                                                ({formatDate(getAlertTimestamp(latestAlert))})
                                                            </span>
                                                            {hasMultiple && (
                                                                <button
                                                                    onClick={() => setExpandedAlerts(prev => ({ ...prev, [groupKey]: !prev[groupKey] }))}
                                                                    style={{
                                                                        background: 'transparent',
                                                                        border: 'none',
                                                                        color: '#1D5769',
                                                                        cursor: 'pointer',
                                                                        fontSize: '0.85em',
                                                                        marginLeft: 'auto',
                                                                        padding: '0',
                                                                        fontWeight: 'bold'
                                                                    }}
                                                                >
                                                                    {isGroupExpanded ? 'Collapse' : 'Expand'}
                                                                </button>
                                                            )}
                                                        </div>
                                                        <p className="mb-3 ms-4">{latestAlert.alert_description_business || latestAlert.alert_description}</p>
                                                        
                                                        {/* Show older alerts if expanded */}
                                                        {hasMultiple && (
                                                            <Collapse in={isGroupExpanded}>
                                                                <div className="ms-4 mb-3">
                                                                    <div className="bg-light p-3 rounded">
                                                                        <strong className="d-block mb-2">Previous Occurrences:</strong>
                                                                        {sortedAlerts.slice(1).map((alert, idx) => (
                                                                            <div key={alert.alertId || idx} className="mb-2 pb-2" style={{ borderBottom: idx < sortedAlerts.length - 2 ? '1px solid #dee2e6' : 'none' }}>
                                                                                <div className="text-muted" style={{ fontSize: '0.85em' }}>
                                                                                    {formatDate(getAlertTimestamp(alert))}
                                                                                </div>
                                                                                <div style={{ fontSize: '0.9em' }}>{alert.alert_description_business || alert.alert_description}</div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </Collapse>
                                                        )}
                                                        
                                                        <div className="ms-4">
                                                            <button
                                                                onClick={() => setExpandedAlerts(prev => ({ ...prev, [`details-${groupKey}`]: !prev[`details-${groupKey}`] }))}
                                                                style={{
                                                                    background: 'transparent',
                                                                    border: 'none',
                                                                    color: '#1D5769',
                                                                    cursor: 'pointer',
                                                                    fontSize: '0.9em',
                                                                    fontWeight: '500',
                                                                    padding: '4px 0',
                                                                    marginBottom: '8px'
                                                                }}
                                                            >
                                                                {expandedAlerts[`details-${groupKey}`] ? 'Hide' : 'Show'} Alert Details
                                                            </button>
                                                            <Collapse in={expandedAlerts[`details-${groupKey}`]}>
                                                                <div className="bg-light p-3 rounded">
                                                                    <div className="row">
                                                                        <div className="col-md-6">
                                                                            <strong className="d-block mb-2">Alert Details:</strong>
                                                                            <ul className="mb-0 ps-3">
                                                                                <li><strong>Category:</strong> {latestAlert.alert_category}</li>
                                                                                <li><strong>Priority:</strong> {latestAlert.alert_priority}</li>
                                                                                <li><strong>Status:</strong> {latestAlert.alert_status}</li>
                                                                                <li><strong>Device ID:</strong> {latestAlert.alert_deviceId}</li>
                                                                            </ul>
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <strong className="d-block mb-2">Additional Info:</strong>
                                                                            <ul className="mb-0 ps-3">
                                                                                <li><strong>Escalation:</strong> {latestAlert.alert_escalation}</li>
                                                                                <li><strong>SMS Sent:</strong> {latestAlert.alert_sms ? 'Yes' : 'No'}</li>
                                                                                <li><strong>Push Notification:</strong> {latestAlert.alert_push_notification ? 'Yes' : 'No'}</li>
                                                                                {latestAlert.alert_trigger_count && (
                                                                                    <li><strong>Trigger Count:</strong> {latestAlert.alert_trigger_count}</li>
                                                                                )}
                                                                            </ul>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </Collapse>
                                                        </div>
                                                    </div>
                                                );
                                            });
                                        })()
                                    ) : (
                                        <p className="text-muted mb-0">No active errors</p>
                                    )}
                                </Card.Body>
                            </Card>

                            {/* Latest Self-Test Results */}
                            <Card className="shadow-sm mb-4">
                                <Card.Body style={{ minHeight: '250px' }}>
                                    <Card.Title>Latest Self-Test Results</Card.Title>
                                    {loadingSelfTest ? (
                                        <div className="text-center py-4">
                                            <Spinner animation="border" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </Spinner>
                                        </div>
                                    ) : selfTestResults ? (
                                        <>
                                            <p className="text-muted mb-3">
                                                {formatDate(selfTestResults.completedDate || selfTestResults.testDate)}
                                            </p>
                                            <div className="mb-3 fw-bold">
                                                Overall Result: <span style={{
                                                    color: getOverallTestStatus().color
                                                }}>{getOverallTestStatus().status}</span>
                                            </div>
                                            <div>
                                                {selfTestResults.testResults && selfTestResults.testResults.length > 0 ? (
                                                    selfTestResults.testResults.map((test, index) => (
                                                        <div key={index} className="ms-3 mb-2 d-flex align-items-center">
                                                            <span style={{
                                                                width: '8px',
                                                                height: '8px',
                                                                borderRadius: '50%',
                                                                backgroundColor: getTestStatusColor(test.test_status),
                                                                marginRight: '8px'
                                                            }} />
                                                            {formatTestName(test.test_name, test.test_code)} {test.test_number}: <span className="ms-2" style={{
                                                                color: getTestStatusColor(test.test_status),
                                                                fontWeight: 'bold'
                                                            }}>{test.test_status === 1 ? 'Pass' : 'Fail'}</span>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="text-muted">No test details available</div>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-muted text-center py-4">
                                            No self-test results available
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col lg={7}>
                            {/* Battery Level History Chart */}
                            <Card className="mb-4 shadow-sm">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <Card.Title className="mb-0">Battery Level History</Card.Title>
                                        <div className="btn-group" role="group">
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${batteryChartView === 'hourly' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => setBatteryChartView('hourly')}
                                            >
                                                Hourly
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${batteryChartView === 'daily' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => setBatteryChartView('daily')}
                                            >
                                                Daily
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${batteryChartView === 'weekly' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => setBatteryChartView('weekly')}
                                            >
                                                Weekly
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${batteryChartView === 'monthly' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => setBatteryChartView('monthly')}
                                            >
                                                Monthly
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${batteryChartView === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => setBatteryChartView('all')}
                                            >
                                                All
                                            </button>
                                        </div>
                                    </div>
                                    <div style={{ height: '350px' }}>
                                        {batteryHistoryData.labels.length > 0 && batteryHistoryData.datasets[0].data.some(value => value !== null) ? (
                                            <Line data={batteryHistoryData} options={chartOptions} />
                                        ) : (
                                            <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                                                <div className="text-center">
                                                    <p className="mb-0">
                                                        {rawBatteryData && rawBatteryData.length > 0 
                                                            ? "No battery level data available for this time frame"
                                                            : "No battery level data available"
                                                        }
                                                    </p>
                                                    <small>
                                                        {rawBatteryData && rawBatteryData.length > 0 
                                                            ? "Try selecting a different time frame to view data"
                                                            : "Data will appear here once the device starts transmitting"
                                                        }
                                                    </small>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>

                            {/* Signal Quality Chart */}
                            <Card className="shadow-sm">
                                <Card.Body style={{ paddingBottom: '20px' }}>
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <Card.Title className="mb-0">Signal Quality History</Card.Title>
                                        <div className="btn-group" role="group">
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${signalQualityChartView === 'hourly' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => setSignalQualityChartView('hourly')}
                                            >
                                                Hourly
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${signalQualityChartView === 'daily' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => setSignalQualityChartView('daily')}
                                            >
                                                Daily
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${signalQualityChartView === 'weekly' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => setSignalQualityChartView('weekly')}
                                            >
                                                Weekly
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${signalQualityChartView === 'monthly' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => setSignalQualityChartView('monthly')}
                                            >
                                                Monthly
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${signalQualityChartView === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => setSignalQualityChartView('all')}
                                            >
                                                All
                                            </button>
                                        </div>
                                    </div>
                                    <div style={{ height: '500px' }}>
                                        {signalQualityHistoryData.labels.length > 0 && signalQualityHistoryData.datasets.length > 0 && signalQualityHistoryData.datasets.some(dataset => dataset.data.some(value => value !== null)) ? (
                                            <Line data={signalQualityHistoryData} options={chartOptions} />
                                        ) : (
                                            <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                                                <div className="text-center">
                                                    <p className="mb-0">
                                                        {rawSignalQualityData && rawSignalQualityData.length > 0 
                                                            ? "No signal quality data available for this time frame"
                                                            : "No signal quality data available"
                                                        }
                                                    </p>
                                                    <small>
                                                        {rawSignalQualityData && rawSignalQualityData.length > 0 
                                                            ? "Try selecting a different time frame to view data"
                                                            : "Data will appear here once the device starts transmitting"
                                                        }
                                                    </small>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
            )}

            {/* Action Confirmation Modal */}
            <Modal
                open={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                BackdropProps={{
                    style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
                }}
            >
                <Box sx={modalStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h4 style={{ margin: 0 }}>Confirm Action</h4>
                        <span 
                            onClick={() => setShowConfirmModal(false)}
                            style={{ 
                                cursor: 'pointer', 
                                fontSize: '24px',
                                marginTop: '-8px'
                            }}
                        >
                            ×
                        </span>
                    </div>
                    <p>{getActionDescription(actionType)}</p>
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                        <MuiButton 
                            variant="outlined"
                            onClick={() => setShowConfirmModal(false)}
                            style={{
                                borderColor: '#ccc',
                                color: '#666',
                                textTransform: 'none'
                            }}
                        >
                            Cancel
                        </MuiButton>
                        <MuiButton 
                            variant="contained"
                            onClick={confirmAction}
                            style={{
                                backgroundColor: '#1D5769',
                                color: 'white',
                                textTransform: 'none'
                            }}
                        >
                            Confirm
                        </MuiButton>
                    </div>
                </Box>
            </Modal>

            <Modal
                open={showClinicalModal}
                onClose={handleCloseModal}
                BackdropProps={{
                    style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
                }}
            >
                <Box sx={modalStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h4 style={{ margin: 0 }}>
                            {clinicalInfo && (clinicalInfo.studyId || clinicalInfo.studySite || clinicalInfo.collectionInfo)
                                ? 'Update Clinical Study Information'
                                : 'Assign to a Clinical Study'}
                        </h4>
                        <span 
                            onClick={handleCloseModal}
                            style={{ 
                                cursor: 'pointer', 
                                fontSize: '24px',
                                marginTop: '-8px'
                            }}
                        >
                            ×
                        </span>
                    </div>
                    <TextField
                        label="Study ID"
                        value={clinicalForm.studyId}
                        onChange={handleFormChange('studyId')}
                        fullWidth
                        variant="outlined"
                        size="small"
                    />
                    <TextField
                        label="Study Site"
                        value={clinicalForm.studySite}
                        onChange={handleFormChange('studySite')}
                        fullWidth
                        variant="outlined"
                        size="small"
                    />
                    <TextField
                        label="Collection Info"
                        value={clinicalForm.collectionInfo}
                        onChange={handleFormChange('collectionInfo')}
                        fullWidth
                        variant="outlined"
                        size="small"
                        multiline
                        rows={4}
                    />
                    <Button 
                        variant="contained"
                        onClick={handleSubmitClinical}
                        fullWidth
                        style={{
                            backgroundColor: '#006969',
                            color: 'white',
                            marginTop: '10px',
                            textTransform: 'none'
                        }}
                    >
                        Save Clinical Information
                    </Button>
                </Box>
            </Modal>

            {/* Notes Modal */}
            <Modal
                open={showNotesModal}
                onClose={() => {
                    setShowNotesModal(false);
                    setNewNote('');
                }}
                BackdropProps={{
                    style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
                }}
            >
                <Box sx={{...modalStyle, width: 500}}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h4 style={{ margin: 0 }}>Add Device Note</h4>
                        <span 
                            onClick={() => {
                                setShowNotesModal(false);
                                setNewNote('');
                            }}
                            style={{ 
                                cursor: 'pointer', 
                                fontSize: '24px',
                                marginTop: '-8px'
                            }}
                        >
                            ×
                        </span>
                    </div>
                    <TextField
                        label="Device Note"
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        fullWidth
                        variant="outlined"
                        size="small"
                        multiline
                        rows={4}
                        placeholder="Enter any device-related issues or notes here..."
                    />
                    <Button 
                        variant="contained"
                        onClick={handleAddNote}
                        fullWidth
                        style={{
                            backgroundColor: '#006969',
                            color: 'white',
                            marginTop: '10px',
                            textTransform: 'none'
                        }}
                    >
                        Add Note
                    </Button>
                </Box>
            </Modal>

            {/* Checkout Modal */}
            <Modal
                open={showCheckoutModal}
                onClose={() => {
                    setShowCheckoutModal(false);
                    setCheckoutForm({ name: '', reason: '', system: '' });
                }}
                BackdropProps={{
                    style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
                }}
            >
                <Box sx={{...modalStyle, width: 400}}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h4 style={{ margin: 0 }}>Device Check Out Card</h4>
                        <span 
                            onClick={() => {
                                setShowCheckoutModal(false);
                                setCheckoutForm({ name: '', reason: '', system: '' });
                            }}
                            style={{ 
                                cursor: 'pointer', 
                                fontSize: '24px',
                                marginTop: '-8px'
                            }}
                        >
                            ×
                        </span>
                    </div>
                    <TextField
                        label="Name"
                        value={checkoutForm.name}
                        onChange={handleCheckoutFormChange('name')}
                        fullWidth
                        variant="outlined"
                        size="small"
                    />
                    <TextField
                        label="Reason"
                        value={checkoutForm.reason}
                        onChange={handleCheckoutFormChange('reason')}
                        fullWidth
                        variant="outlined"
                        size="small"
                    />
                    <TextField
                        label="System"
                        value={checkoutForm.system}
                        onChange={handleCheckoutFormChange('system')}
                        fullWidth
                        variant="outlined"
                        size="small"
                    />
                    <Button 
                        variant="contained"
                        onClick={handleCheckout}
                        fullWidth
                        style={{
                            backgroundColor: '#006969',
                            color: 'white',
                            marginTop: '10px',
                            textTransform: 'none'
                        }}
                    >
                        Check Out
                    </Button>
                </Box>
            </Modal>
        </>
    );
};

export default DeviceInformationContent; 
